import { CheckCircleFilled, CloseCircleFilled, LockOutlined } from '@ant-design/icons'
import { Alert, App, Form, Input, Modal } from 'antd'
import { useMemo, useState } from 'react'

import { loginSuccess } from '../app/authSlice'
import { useAppDispatch } from '../app/store'
import { authApi } from '../services/endpoints'

import './ChangePasswordModal.css'

/**
 * شروط كلمة المرور — نسخة الواجهة من PasswordRules بالخادم (المرجع): تُعرض حيّةً أثناء الكتابة،
 * كل شرطٍ أحمر حتى يُستوفى فيصير أخضر، والحفظ مقفول حتى تكتمل كلها.
 */
export const PASSWORD_RULES: { key: string; label: string; test: (p: string) => boolean }[] = [
  { key: 'len', label: '8 أحرف على الأقل', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'حرف إنجليزي كبير (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'حرف إنجليزي صغير (a-z)', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'رقم واحد على الأقل (0-9)', test: (p) => /[0-9]/.test(p) },
  { key: 'space', label: 'بلا مسافات', test: (p) => p.length > 0 && !/\s/.test(p) },
]

interface Props {
  open: boolean
  onClose: () => void
}

export default function ChangePasswordModal({ open, onClose }: Props) {
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm<{ current: string; next: string; confirm: string }>()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checks = useMemo(
    () => [
      ...PASSWORD_RULES.map((r) => ({ key: r.key, label: r.label, ok: r.test(next) })),
      {
        key: 'diff',
        label: 'مختلفة عن الحالية',
        ok: next.length > 0 && current.length > 0 && next !== current,
      },
      { key: 'match', label: 'التأكيد مطابق', ok: confirm.length > 0 && confirm === next },
    ],
    [next, current, confirm],
  )
  const passed = checks.filter((c) => c.ok).length
  const allOk = passed === checks.length && current.length > 0
  const strength = Math.round((PASSWORD_RULES.filter((r) => r.test(next)).length / 5) * 100)

  const reset = () => {
    form.resetFields()
    setCurrent('')
    setNext('')
    setConfirm('')
    setError(null)
  }
  const close = () => {
    reset()
    onClose()
  }

  const submit = async () => {
    if (!allOk) return
    setSaving(true)
    setError(null)
    try {
      const res = await authApi.changePassword(current, next)
      // جلسةٌ جديدة لهذا الجهاز — الأجهزة الأخرى تخرج عند انتهاء رمزها
      dispatch(loginSuccess({ token: res.token, user: res.user, refreshToken: res.refreshToken }))
      message.success('تغيّرت كلمة المرور ✓ — استخدمها بالدخول القادم')
      close()
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'تعذّر تغيير كلمة المرور')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title={
        <span>
          <LockOutlined /> تغيير كلمة المرور
        </span>
      }
      okText="حفظ كلمة المرور"
      cancelText="إلغاء"
      onOk={submit}
      onCancel={close}
      confirmLoading={saving}
      okButtonProps={{ disabled: !allOk }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} className="cpw-form">
        <Form.Item name="current" label="كلمة المرور الحالية">
          <Input.Password
            autoComplete="current-password"
            value={current}
            onChange={(e) => {
              setCurrent(e.target.value)
              setError(null)
            }}
          />
        </Form.Item>
        <Form.Item name="next" label="كلمة المرور الجديدة">
          <Input.Password
            autoComplete="new-password"
            value={next}
            onChange={(e) => {
              setNext(e.target.value)
              setError(null)
            }}
          />
        </Form.Item>
        {next.length > 0 && (
          <div
            className="cpw-meter"
            data-level={strength < 60 ? 'weak' : strength < 100 ? 'mid' : 'strong'}
            role="meter"
            aria-label="قوة كلمة المرور"
            aria-valuenow={strength}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${strength}%` }} />
          </div>
        )}
        <Form.Item name="confirm" label="تأكيد كلمة المرور الجديدة">
          <Input.Password
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Form.Item>
      </Form>

      <ul className="cpw-rules" aria-label="شروط كلمة المرور">
        {checks.map((c) => (
          <li key={c.key} className={c.ok ? 'is-ok' : 'is-bad'} data-ok={c.ok}>
            {c.ok ? <CheckCircleFilled /> : <CloseCircleFilled />}
            <span>{c.label}</span>
          </li>
        ))}
      </ul>

      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  )
}
